import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, GraduationCap, Calculator, UserPlus, Users, LogOut, Bell, Search, Settings, Home, TrendingUp, Calendar, Filter, Clock, CheckSquare, PieChart, ArrowDown, ArrowUp, X, AlignJustify, MessageSquare, Mail } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import SidebarNav from './SidebarNav';
import GoodbyeScreen from './GoodbyeScreen';
import MessageForm from './MessageForm';
import LatestCommunications from './LatestCommunications';
import UpcomingEvents from './UpcomingEvents';
import { motion, AnimatePresence } from 'framer-motion';
import SessionManager from '../components/sessions/SessionManager';
// EN HAUT DU FICHIER - AJOUTER CETTE LIGNE après les autres imports
import SchoolHeader from '../components/SchoolHeader';
import useSchoolParams from '../hooks/useSchoolParams';

// Ajout du badge automatique et du modal de notifications
import NotificationBadge from './NotificationBadge';
import { useFeatures } from '../contexts/FeaturesContext';
import NotificationModal from './NotificationModal';
import useNotificationCounter from '../hooks/useNotificationCounter';
import { installerDeverrouillageAudio, jouerSon } from '../utils/sonsKlasys';
import { cleStockage, reglagesSonMemorises } from '../components/alertesCritiques/apiAlertesCritiques';
import AlertesCritiques from '../components/alertesCritiques/AlertesCritiques';
import BoutonAlertesCritiques from '../components/alertesCritiques/BoutonAlertesCritiques';
import PanneauLateralAlertes, {
  LARGEUR_GRANDE_TAILLE,
  TAILLE_GRANDE,
  tailleLateraleMemorisee
} from '../components/alertesCritiques/PanneauLateralAlertes';
import {
  LARGEUR_POUSSE,
  panneauLateralMemorise,
  useEcranAuMoins
} from '../components/alertesCritiques/useAlertesCritiques';
import NotreEcole from './NotreEcole';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import ProfileSettingsPopover from '../components/ProfileSettingsPopover';



// Style pour éviter le décalage lors de l'ouverture des modaux
const noShiftStyle = document.createElement('style');
noShiftStyle.textContent = `
  body.modal-open {
    overflow: hidden;
    padding-right: var(--scrollbar-width);
  }
`;
document.head.appendChild(noShiftStyle);

// Fonction utilitaire pour calculer la largeur de la barre de défilement
const getScrollbarWidth = () => {
  // Créer un div avec une barre de défilement
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  document.body.appendChild(outer);

  // Créer un div intérieur
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculer la différence de largeur
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Nettoyer
  outer.parentNode.removeChild(outer);

  return scrollbarWidth;
};

const parseStoredUser = () => {
  try {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error('Erreur lecture utilisateur local:', error);
    return null;
  }
};

const getSidebarPreferenceStorageKey = (user = parseStoredUser()) => {
  const tenant = user?.tenant_subdomain || window.location.hostname || 'default';
  const userId = user?.id || user?.identifiant || 'anonymous';
  return `klasys:${tenant}:${userId}:sidebarDefaultCollapsed`;
};

const readBooleanPreference = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
};

const readSidebarDefaultCollapsedPreference = () => {
  const user = parseStoredUser();
  const cachedPreference = readBooleanPreference(
    localStorage.getItem(getSidebarPreferenceStorageKey(user))
  );

  if (cachedPreference !== null) return cachedPreference;

  const storedPreference = readBooleanPreference(user?.preferences?.sidebar_default_collapsed);
  return storedPreference ?? false;
};

// Son de la cloche. Le repère est `dernierId`, le plus grand id des
// notifications non lues et non archivées renvoyé par
// /api/notifications/count : il monte quand une notification arrive. Comparer
// des nombres ferait sonner un désarchivage ou une lecture annulée. La
// référence est gardée dans le navigateur par école, utilisateur et année, et
// ne fait que monter : plusieurs onglets la partagent, le premier qui voit un
// nouvel id l'écrit et sonne, les autres trouvent la référence déjà à jour et
// se taisent.
//
// La première lecture de chaque montage de la coquille (connexion, F5, clic
// dans le tiroir qui recharge la page) sert de référence et ne sonne jamais :
// ce qui est arrivé pendant l'absence se voit à la pastille, pas à l'oreille.
// Elle pose max(référence stockée, dernierId), et « 0 » s'il n'y a aucune non
// lue : la première notification qui arrive ensuite sonne. Après elle, une
// référence absente vaut 0.
//
// Le repère monte aussi quand on DÉSARCHIVE une non lue plus récente que la
// référence (archivée sans être lue sur un autre poste, ou avant la première
// lecture de ce navigateur) : ce n'est pas une arrivée. Le tiroir relève donc
// la référence jusqu'à elle avant de rendre la notification à la liste
// (releverReferenceNotification).
const cleRepereNotification = () => cleStockage('notifications:dernierVu');

const sonnerSiNouvelleNotification = (dernierId, premiereLecture) => {
  let cle;
  let brut;
  try {
    cle = cleRepereNotification();
    brut = localStorage.getItem(cle);
  } catch {
    return; // Stockage interdit : sans référence partagée, on ne sonne pas.
  }
  const valeur = brut === null ? NaN : Number(brut);
  const reference = Number.isFinite(valeur) ? valeur : 0;

  if (premiereLecture) {
    const posee = Math.max(reference, dernierId ?? 0);
    if (brut === null || posee !== valeur) {
      try { localStorage.setItem(cle, String(posee)); } catch { /* sans référence, rien */ }
    }
    return;
  }
  if (dernierId == null || dernierId <= reference) return;

  // La référence d'abord, le son ensuite : une écriture refusée ferait sonner
  // à chaque tour de sondage.
  try {
    localStorage.setItem(cle, String(dernierId));
  } catch {
    return;
  }
  if (reglagesSonMemorises().son_notifications) jouerSon('notification');
};

// Une non lue remise dans la liste par le tiroir (désarchivée) : la référence
// monte jusqu'à son id, pour que le prochain relevé du compteur ne la prenne
// pas pour une arrivée. Les ids ne font que croître : une notification qui
// arrivera ensuite aura un id plus grand et sonnera quand même.
const releverReferenceNotification = (id) => {
  const valeurId = Number(id);
  if (!Number.isFinite(valeurId) || valeurId <= 0) return;
  try {
    const cle = cleRepereNotification();
    const brut = localStorage.getItem(cle);
    const valeur = brut === null ? NaN : Number(brut);
    const reference = Number.isFinite(valeur) ? valeur : 0;
    if (valeurId > reference) localStorage.setItem(cle, String(valeurId));
  } catch {
    // Stockage interdit : la cloche ne sonne de toute façon pas sans référence.
  }
};

// L'année de la session est-elle CLÔTURÉE ? Le drapeau du navigateur
// `annee_scolaire_read_only` ne le dit pas : le serveur le pose aussi pour
// l'année À VENIR, ni active ni clôturée (authController, lireAnnee), où l'on
// écrit pourtant (le verrou du serveur ne refuse que la clôture,
// cloturePermissions). La ligne de l'année gardée à la connexion
// (user.annee_scolaire) porte est_cloturee ; à défaut, ou si elle ne
// correspond pas à l'année de la session, on retombe sur le drapeau.
const anneeDeSessionCloturee = (user, drapeauLectureSeule) => {
  const annee = user?.annee_scolaire;
  if (!annee || typeof annee !== 'object' || annee.est_cloturee === undefined || annee.est_cloturee === null) {
    return drapeauLectureSeule;
  }
  let idSession = null;
  try {
    idSession = localStorage.getItem('annee_scolaire_id');
  } catch {
    idSession = null;
  }
  if (idSession && annee.id != null && String(annee.id) !== String(idSession)) return drapeauLectureSeule;
  return Number(annee.est_cloturee) === 1;
};

// --- Alertes critiques : bouton de la barre et panneau latéral -------------------
//
// Le bouton « Alertes » (BoutonAlertesCritiques, à gauche de la cloche) ouvre
// et ferme le panneau latéral des alertes critiques (PanneauLateralAlertes),
// à droite, sous la barre. Tout ce qui bouge (résumé, compte, relectures,
// pause pendant une fenêtre, hauteur de la barre) vit dans le fournisseur
// AlertesCritiques et dans ces deux composants, qui lisent son contexte : la
// coquille ne suit que l'ouverture du panneau (onPanneauOuvertChange), pour
// pousser la page et replier le menu. Ni la page en cours ni le menu ne se
// rendent donc à nouveau à chaque relecture.
//
// Mise en page du panneau ouvert (GO du 16/10/2026) :
//   - dès 1 280 px (LARGEUR_POUSSE), il POUSSE la page (marge droite xl:mr-80,
//     même mécanisme que ml-72 / ml-20) ;
//   - de 1 280 à 1 599 px, le menu de gauche se replie tant qu'il est ouvert
//     (état dérivé, voir menuReplieEffectif) ;
//   - sous 1 280 px, il se pose PAR-DESSUS la page, sans voile, la page reste
//     cliquable (« Corriger » le replie alors, dans le fournisseur) ;
//   - sous 768 px, pleine largeur (dans PanneauLateralAlertes).
// Le panneau se cale sous la barre à sa hauteur RÉELLE (~90 px avec un
// slogan) : la coquille lui passe la barre (barreRef), il la mesure.
//
// Deux tailles (GO du 16/10/2026) : standard 320 px, grande 520 px. Le
// panneau dit à la coquille le CHOIX de l'agent (onTailleChange) ; la grande
// taille ne s'applique qu'à partir de 1 600 px, par la même règle CSS des
// deux côtés (panneau : min-[1600px]:w-[520px], page :
// min-[1600px]:mr-[520px]), donc la marge suit toujours la largeur affichée.
// En grande taille, la page garde au moins ~900 px : le menu se replie (même
// état dérivé) sous 1 920 px ; dès 1 920 px il reste ouvert
// (1 920 - 288 - 520 = 1 112 px de page).

/** Dès cette largeur, le menu reste ouvert à côté du panneau latéral (taille standard). */
const LARGEUR_MENU_A_COTE = 1600;
/** Dès cette largeur, le menu reste ouvert à côté du panneau en grande taille. */
const LARGEUR_MENU_A_COTE_GRANDE = 1920;

const persistSidebarDefaultCollapsedLocally = (value, user = parseStoredUser()) => {
  localStorage.setItem(getSidebarPreferenceStorageKey(user), value ? 'true' : 'false');
};

const updateStoredUserPreferences = (preferences = {}) => {
  const storedUser = parseStoredUser();
  if (!storedUser) return null;

  const nextUser = {
    ...storedUser,
    preferences: {
      ...(storedUser.preferences || {}),
      ...preferences
    }
  };

  localStorage.setItem('user', JSON.stringify(nextUser));
  return nextUser;
};

const AdminHome = () => {


  // Etat pour les filtres
  const { schoolInfo } = useSchoolParams();
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState({
    classe: '',
    niveau: '',
    statut: '',
    dateDebut: '',
    dateFin: ''
  });



  return (
    <div className="p-8 bg-transparent w-full">
      {/* Container principal */}
      <div className="w-full mb-8">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ duration: 0.5 }}
        >
         <motion.header 
          className="text-center mb-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-2xl font-bold text-[#246990]">
            {schoolInfo.titre_accueil || 'Titre non configuré - Paramètres > Établissement'}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            {schoolInfo.description_accueil || 'Description non configurée - Paramètres > Établissement'}
          </p>
        </motion.header>
        </motion.div>
      </div>


      {/* Section des événements - Pleine largeur */}
      <div className="w-full mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center text-[#246990] mb-6">
          </div>
          <UpcomingEvents />
        </div>
      </div>

      {/* Section des communications - Pleine largeur */}
      <div className="w-full mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center text-[#246990] mb-6">
          </div>
          <LatestCommunications />
        </div>
      </div>


      {/* Section "Notre école" */}
      <div className="w-full mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <NotreEcole />
        </div>
      </div>

    </div>
  );
};

const AdministrationLayout = () => {
  
  const [showGoodbyeScreen, setShowGoodbyeScreen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => readSidebarDefaultCollapsedPreference());
  const [sidebarDefaultCollapsed, setSidebarDefaultCollapsed] = useState(() => readSidebarDefaultCollapsedPreference());
  const [sidebarPreferenceSaving, setSidebarPreferenceSaving] = useState(false);
  const [sidebarPreferenceError, setSidebarPreferenceError] = useState('');
  // Panneau latéral des alertes critiques : ouvert ou non, c'est tout ce que
  // la coquille en sait (le fournisseur le lui dit). État initial pris dans
  // l'onglet (F5, clic d'une notification qui recharge la page) : la page est
  // poussée dès le premier rendu, sans à-coup.
  const [panneauAlertesOuvert, setPanneauAlertesOuvert] = useState(panneauLateralMemorise);
  // L'agent a touché au menu pendant que le panneau le repliait : le repli
  // forcé est levé jusqu'à la fermeture du panneau.
  const [repliMenuAnnule, setRepliMenuAnnule] = useState(false);
  // La taille CHOISIE du panneau (standard / grande), retenue par l'agent :
  // lue au montage, puis donnée par le panneau (onTailleChange).
  const [tailleAlertes, setTailleAlertes] = useState(tailleLateraleMemorisee);
  const ecranPousse = useEcranAuMoins(LARGEUR_POUSSE);
  const menuACote = useEcranAuMoins(LARGEUR_MENU_A_COTE);
  const grandeTaillePossible = useEcranAuMoins(LARGEUR_GRANDE_TAILLE);
  const menuACoteGrande = useEcranAuMoins(LARGEUR_MENU_A_COTE_GRANDE);
  // La barre du haut : le panneau latéral la mesure (ResizeObserver) et se
  // cale juste dessous. Aucun état ici : la coquille ne se rend pas à nouveau.
  const barreRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showDossierMenu, setShowDossierMenu] = useState(
    location.pathname.includes('/dossier-eleves')
  );
  
  // Etats et localStorage pour notifications supprimes
  // const [unreadNotificationCount, setUnreadNotificationCount] = useState(() => {
  //   const savedNotifications = localStorage.getItem('notifications');
  //   if (savedNotifications) {
  //     const notifications = JSON.parse(savedNotifications);
  //     return notifications.filter(n => !n.read).length;
  //   }
  //   return 2; // Valeur par défaut pour démonstration
  // });
  
  // Etat pour les messages
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // La messagerie est un module vendu : l'en-tete doit s'aligner sur le menu.
  //
  // On attend la reponse avant d'afficher l'icone. L'inverse la ferait
  // apparaitre puis disparaitre a chaque connexion — une entree qui se retire
  // sous les yeux se lit comme un bug, alors qu'une entree qui arrive ne se
  // remarque pas.
  const { estActive, chargement: chargementLicence } = useFeatures();
  const messagerieOuverte = !chargementLicence && estActive('module.messagerie');

  // Le compteur de la cloche : une seule instance, pour la pastille et le
  // tiroir (chacun interrogeait le serveur pour son compte).
  const {
    unreadCount: notificationsNonLues,
    dernierId: dernierIdNotification,
    dernierIdConnu: repereNotificationRecu,
    rafraichir: rafraichirCompteurNotifications
  } = useNotificationCounter();

  // Le son se déverrouille au premier geste : l'arrivée dans l'administration
  // recharge la page, le clic « Se connecter » ne compte pas pour elle.
  useEffect(() => {
    installerDeverrouillageAudio();
  }, []);

  // La première réponse qui porte le repère (même à null) sert de référence,
  // sans son. `repereNotificationRecu` relance l'effet à cette réponse-là,
  // même quand le repère reste null ; tant qu'il est faux (pas encore de
  // réponse, ou serveur sans le champ), rien n'est écrit ni joué.
  const premiereLectureNotificationRef = useRef(true);
  useEffect(() => {
    if (!repereNotificationRecu) return;
    const premiereLecture = premiereLectureNotificationRef.current;
    premiereLectureNotificationRef.current = false;
    sonnerSiNouvelleNotification(dernierIdNotification, premiereLecture);
  }, [repereNotificationRecu, dernierIdNotification]);


  // Gerer le overflow du body quand un modal est ouvert
  useEffect(() => {
    const hasModal = showMessage || showNotifications; // Garder showNotifications pour le modal
    if (hasModal) {
      const scrollbarWidth = getScrollbarWidth();
      document.body.classList.add('modal-open');
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showMessage, showNotifications]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedInNew') === 'true';
    if (!isLoggedIn) {
      navigate('/connexion', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadUnreadMessages = async () => {
      try {
        const result = await api.messagerie.getUnreadCount();
        const count = result?.data?.unreadCount ?? result?.data?.count ?? 0;
        if (isMounted) setUnreadMessageCount(count);
      } catch (error) {
        console.error('Erreur chargement compteur messagerie:', error);
        if (isMounted) setUnreadMessageCount(0);
      }
    };

    // Inutile de demander le compteur d'une messagerie non souscrite : le
    // serveur repondrait 403 et la console se remplirait d'erreurs qui n'en
    // sont pas.
    if (messagerieOuverte) {
      loadUnreadMessages();
    } else {
      setUnreadMessageCount(0);
    }

    return () => {
      isMounted = false;
    };
  }, [messagerieOuverte]);
  
  // Chargement du compteur de notifications depuis localStorage supprime
  // useEffect(() => {
  //   const savedNotifications = localStorage.getItem('notifications');
  //   if (savedNotifications) {
  //     const notifications = JSON.parse(savedNotifications);
  //     const unreadCount = notifications.filter(n => !n.read).length;
  //     setUnreadNotificationCount(unreadCount);
  //   }
  // }, []);
  
 
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      console.log("Données utilisateur stockées:", parsedUser); // Pour déboguer
      return parsedUser;
    }
    return {
      name: "Invité",
      role: "Non connecté",
      avatar: "/api/placeholder/40/40"
    };
  });
  const applySidebarPreferencesLocally = useCallback((preferences = {}) => {
    const sidebarDefault = readBooleanPreference(preferences.sidebar_default_collapsed);

    if (sidebarDefault !== null) {
      persistSidebarDefaultCollapsedLocally(sidebarDefault);
    }

    updateStoredUserPreferences(preferences);
    setUser(prev => ({
      ...prev,
      preferences: {
        ...(prev?.preferences || {}),
        ...preferences
      }
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadUserPreferences = async () => {
      try {
        const result = await api.userPreferences.getMe();
        const preferences = result?.data;
        const sidebarDefault = readBooleanPreference(preferences?.sidebar_default_collapsed);

        if (isMounted && result?.success && sidebarDefault !== null) {
          applySidebarPreferencesLocally(preferences);
          setSidebarDefaultCollapsed(sidebarDefault);
          setIsNavCollapsed(sidebarDefault);
          setSidebarPreferenceError('');
        }
      } catch (error) {
        console.error('Erreur chargement preference sidebar:', error);
      }
    };

    loadUserPreferences();

    return () => {
      isMounted = false;
    };
  }, [applySidebarPreferencesLocally, user?.id, user?.tenant_subdomain]);

  const handleSidebarDefaultCollapsedChange = async (nextValue) => {
    const nextPreference = Boolean(nextValue);
    const nextPreferences = { sidebar_default_collapsed: nextPreference };

    setSidebarDefaultCollapsed(nextPreference);
    setIsNavCollapsed(nextPreference);
    setSidebarPreferenceError('');
    applySidebarPreferencesLocally(nextPreferences);

    try {
      setSidebarPreferenceSaving(true);
      const result = await api.userPreferences.updateMe(nextPreferences);

      if (!result?.success) {
        throw new Error(result?.message || result?.error || 'Sauvegarde impossible');
      }

      const savedPreferences = result.data || nextPreferences;
      const savedSidebarDefault = readBooleanPreference(savedPreferences.sidebar_default_collapsed);
      applySidebarPreferencesLocally(savedPreferences);

      if (savedSidebarDefault !== null) {
        setSidebarDefaultCollapsed(savedSidebarDefault);
        setIsNavCollapsed(savedSidebarDefault);
      }
    } catch (error) {
      console.error('Erreur sauvegarde preference sidebar:', error);
      setSidebarPreferenceError('Non enregistré sur le serveur');
    } finally {
      setSidebarPreferenceSaving(false);
    }
  };
  const isReadOnlyYear = localStorage.getItem('annee_scolaire_read_only') === 'true';
  const anneeCloturee = anneeDeSessionCloturee(user, isReadOnlyYear);
  const selectedYearName = localStorage.getItem('annee_scolaire_nom') || user?.annee_scolaire?.nom || '';

  // Le panneau latéral n'existe pas en année clôturée (le fournisseur y est
  // inactif et le ferme ; la garde ici évite un rendu poussé entre-temps).
  const panneauAlertesAffiche = panneauAlertesOuvert && !anneeCloturee;
  // Grande taille choisie (la marge en suit la règle CSS) et affichée (dès 1 600 px).
  const grandeTailleChoisie = tailleAlertes === TAILLE_GRANDE;
  const panneauAlertesGrand = panneauAlertesAffiche && grandeTailleChoisie && grandeTaillePossible;

  // Repli du menu par le panneau : un état DÉRIVÉ. De 1 280 à 1 599 px, le
  // panneau ouvert replie le menu ; en grande taille, jusqu'à 1 919 px. Il
  // n'écrit jamais isNavCollapsed ni la préférence de l'agent
  // (loadUserPreferences, qui répond après le montage, rouvrirait sinon le
  // menu sous un panneau rétabli au F5). Le menu revient à la fermeture du
  // panneau, sauf si l'agent y a touché entre-temps.
  const menuGardeSaPlace = panneauAlertesGrand ? menuACoteGrande : menuACote;
  const repliParPanneau = panneauAlertesAffiche && ecranPousse && !menuGardeSaPlace && !repliMenuAnnule;
  const menuReplieEffectif = isNavCollapsed || repliParPanneau;

  useEffect(() => {
    if (!panneauAlertesAffiche) setRepliMenuAnnule(false);
  }, [panneauAlertesAffiche]);

  // Un clic de l'agent sur le menu (bouton du bas) ou sur sa préférence lève
  // le repli forcé : son choix l'emporte jusqu'à la fermeture du panneau.
  const changerMenuReplie = useCallback((valeur) => {
    if (repliParPanneau) setRepliMenuAnnule(true);
    setIsNavCollapsed(Boolean(valeur));
  }, [repliParPanneau]);

  // L'ouverture exceptionnelle d'une année clôturée, posée depuis le CIK :
  // l'archive reste clôturée, mais ce qu'elle couvre s'écrit jusqu'à
  // l'échéance. Le bandeau le dit au lieu de « lecture seule ». Relue toutes
  // les cinq minutes : une refermeture depuis le CIK doit se voir sans
  // reconnexion.
  const [ouvertureExceptionnelle, setOuvertureExceptionnelle] = useState(null);
  useEffect(() => {
    let idAnnee = null;
    try {
      idAnnee = localStorage.getItem('annee_scolaire_id');
    } catch {
      idAnnee = null;
    }
    if (!anneeCloturee || !idAnnee) {
      setOuvertureExceptionnelle(null);
      return undefined;
    }
    let actif = true;
    const lire = async () => {
      const r = await api.anneesScolaires.getOuvertureExceptionnelle(idAnnee);
      if (actif) setOuvertureExceptionnelle(r?.success ? r.ouverture || null : null);
    };
    lire();
    const minuteur = setInterval(lire, 5 * 60 * 1000);
    return () => {
      actif = false;
      clearInterval(minuteur);
    };
  }, [anneeCloturee]);

  const formaterEcheance = (valeur) => {
    const [jour, heure] = String(valeur || '').split(' ');
    const [a, m, j] = String(jour || '').split('-');
    return a && m && j ? `${j}/${m}/${a}${heure ? ` à ${heure}` : ''}` : valeur;
  };

const handleLogout = async () => {
  console.log('[Frontend] Debut deconnexion...');
  
  try {
    // Protection contre double clic
    if (isExiting) {
      console.log('[Frontend] Logout deja en cours, ignore...');
      return;
    }
    
    setIsExiting(true);
    
    // Appel serveur avec timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    try {
      console.log('[Frontend] Appel logout serveur...');
      
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        signal: controller.signal // Timeout
      });
      
      clearTimeout(timeoutId);
      console.log('[Frontend] Reponse serveur recue:', response.status);
      
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        console.warn('[Frontend] Timeout logout serveur (5s)');
      } else {
        console.error('[Frontend] Erreur fetch logout:', fetchError);
      }
      // Continue le logout côté client même si serveur échoue
    }
    
    // Nettoyage cote client force
    console.log('[Frontend] Nettoyage localStorage...');
    
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedInNew');
      localStorage.removeItem('token');
      console.log('[Frontend] localStorage nettoye');
    } catch (storageError) {
      console.error('[Frontend] Erreur nettoyage localStorage:', storageError);
    }
    
    // Affichage ecran goodbye
    console.log('[Frontend] Affichage ecran goodbye...');
    setShowGoodbyeScreen(true);
    
    // Redirection de secours
    setTimeout(() => {
      console.log('[Frontend] Redirection de secours...');
      window.location.href = '/connexion';
    }, 3000); // 3s après
    
  } catch (globalError) {
    console.error('[Frontend] Erreur globale logout:', globalError);
    
    // Logout de secours total
    localStorage.clear();
    window.location.href = '/connexion';
  }
};
  const isAdminHome = location.pathname === '/administration' || location.pathname === '/administration/';

  return (
    <>
    {showGoodbyeScreen ? (
      <GoodbyeScreen user={user} />
    ) : (
    <div className={`flex flex-col h-screen ${isExiting ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
      {/* Alertes critiques : un FOURNISSEUR (une seule instance de
          l'ordonnanceur) qui enveloppe la barre, le panneau latéral et les
          pages. Il rend aussi le panneau central : à la connexion, puis à
          intervalle tant qu'il reste à faire ; il ne s'ouvre pas par-dessus
          un tiroir (cloche, messagerie), il attend qu'il se ferme ; il est
          suspendu tant que le panneau latéral est ouvert.
          Inactif (ni bouton, ni panneau) dans une session ouverte sur une
          année CLÔTURÉE : les alertes portent sur les années ouvertes, leurs
          liens ouvriraient l'archive et toute correction ou tout constat y
          serait refusé (423). Actif en revanche sur l'année À VENIR, que le
          bandeau dit « en lecture seule » mais où le serveur accepte les
          écritures : c'est là que se corrigent les réductions recopiées par
          la réinscription. Toujours monté (actif plutôt qu'un montage sous
          condition) : la page ne se remonte pas si l'année change. */}
      <AlertesCritiques
        actif={!anneeCloturee}
        autreFenetreOuverte={showNotifications || showMessage}
        onPanneauOuvertChange={setPanneauAlertesOuvert}
      >
      {/* Barre supérieure avec dégradé */}
      <div ref={barreRef} className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#246990] to-[#3084b5] text-white">
         {/* gap-4 : l'écart minimal entre l'école et le bouton « Alertes » quand la place manque. */}
         <div className="flex justify-between items-center gap-4 px-4 py-4">
          <div className="flex items-center space-x-8 flex-shrink-0">
            <SchoolHeader />
          </div>
 
          <div className="flex min-w-0 items-center space-x-6">
            {/* Bouton « Alertes » : le panneau latéral des alertes critiques.
                Caché en année clôturée et sans détecteur ouvert. */}
            <BoutonAlertesCritiques />

            {/* Nouveau : badge de notifications automatique */}
            <NotificationBadge
              onClick={() => setShowNotifications(!showNotifications)}
              unreadCount={notificationsNonLues}
              className="text-white [&_svg]:text-white [&_.bg-red-500]:bg-red-500"
            />

            {/* Bouton de messagerie.
                Il vit dans l'en-tete et non dans le menu lateral : il echappait
                donc au filtrage par licence, et restait visible dans une ecole
                qui n'a pas souscrit la messagerie. */}
            {messagerieOuverte && (
              <button
                onClick={() => setShowMessage(!showMessage)}
                className="p-2 hover:bg-white/10 rounded-lg relative"
              >
                <Mail size={22} className="text-white" />
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 rounded-lg flex items-center justify-center text-xs font-bold">
                    {unreadMessageCount}
                  </span>
                )}
              </button>
            )}

            {/* Quand la place manque (1 280 px et un nom d'école long : le
                bouton « Alertes » a pris ~120 px), le nom de l'agent se
                tronque, jamais « Déconnexion » qui sortirait de l'écran :
                min-w-0 sur les deux groupes, et le bouton du profil (un
                <button> garde sinon la largeur de son texte) borné à son
                enveloppe, de 120 px (l'avatar et quelques lettres, jamais
                un liseré) à 280 px. */}
            <div className="flex min-w-0 items-center space-x-4">
              <div className="min-w-[120px] max-w-[280px] [&>*]:max-w-full">
                <ProfileSettingsPopover user={user} onUserChange={setUser} />
              </div>
              <div className="text-sm font-semibold text-white">
                <button 
                  onClick={handleLogout}
                  className="h-9 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center space-x-2 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
       <SessionManager />

      <div className="flex flex-1 pt-20">
        {/* Barre latérale avec dégradé. Le repli reçu est l'état DÉRIVÉ
            (choix de l'agent, ou repli par le panneau des alertes) ; un clic
            de l'agent passe par changerMenuReplie, qui lève le repli forcé. */}
        <div className="fixed left-0 top-16 bottom-0 z-40 h-full">
          <div className="h-full"> {/* Conteneur supplémentaire pour garantir la hauteur */}
            <SidebarNav 
              isNavCollapsed={menuReplieEffectif}
              setIsNavCollapsed={changerMenuReplie}
              sidebarDefaultCollapsed={sidebarDefaultCollapsed}
              onSidebarDefaultCollapsedChange={(valeur) => {
                if (repliParPanneau) setRepliMenuAnnule(true);
                handleSidebarDefaultCollapsedChange(valeur);
              }}
              sidebarPreferenceSaving={sidebarPreferenceSaving}
              sidebarPreferenceError={sidebarPreferenceError}
            />
          </div>
        </div>
        
        {/* Contenu principal. Panneau des alertes ouvert, dès 1 280 px (xl),
            la page lui laisse sa place à droite : 320 px, ou 520 px dès
            1 600 px en grande taille (la règle CSS du panneau). */}
        <div
          className={`flex-1 overflow-auto ${menuReplieEffectif ? 'ml-20' : 'ml-72'} ${
            panneauAlertesAffiche ? `xl:mr-80${grandeTailleChoisie ? ' min-[1600px]:mr-[520px]' : ''}` : ''
          } transition-[margin-right] duration-300 ease-out`}
        >
          {isReadOnlyYear && ouvertureExceptionnelle && (
            <div className="mx-6 mt-5 rounded-lg border border-[#246990]/30 bg-[#246990]/5 px-4 py-3 text-sm text-[#1b4f6d] shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-[#246990] px-2 py-1 text-xs font-bold uppercase tracking-wide text-white">
                  Ouverture exceptionnelle
                </span>
                <span className="font-semibold">
                  Année scolaire {selectedYearName || 'archivée'} - {ouvertureExceptionnelle.libelle}
                </span>
                <span>
                  Modifiable jusqu'au {formaterEcheance(ouvertureExceptionnelle.expire_le)}.
                  {ouvertureExceptionnelle.perimetre === 'notes' && ' Le reste de l\'archive reste en lecture seule.'}
                </span>
              </div>
            </div>
          )}
          {isReadOnlyYear && !ouvertureExceptionnelle && (
            <div className="mx-6 mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-bold uppercase tracking-wide text-amber-700">
                  Lecture seule
                </span>
                <span className="font-semibold">
                  Annee scolaire {selectedYearName || 'archivee'}
                </span>
                <span className="text-amber-700">
                  Vous pouvez consulter les donnees, mais aucune modification ne sera enregistree.
                </span>
              </div>
            </div>
          )}
          {isAdminHome ? <AdminHome /> : <Outlet />}
        </div>
      </div>
      
      {/* Panneau latéral des alertes critiques (bouton « Alertes ») : un
          <aside> à droite, sous la barre, jamais une fenêtre. Il donne la
          taille choisie (standard / grande) pour la marge et le menu. */}
      <PanneauLateralAlertes barreRef={barreRef} onTailleChange={setTailleAlertes} />

      {/* Modals (déplacés en dehors de la structure pour éviter les problèmes de positionnement) */}
      
      {/* Modal des notifications */}
      <AnimatePresence>
        {showNotifications && (
          <NotificationModal
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
            unreadCount={notificationsNonLues}
            dernierId={dernierIdNotification}
            rafraichirCompteur={rafraichirCompteurNotifications}
            noterNotificationVue={releverReferenceNotification}
          />
        )}
      </AnimatePresence>
      
      {/* Modal de messagerie */}
      <AnimatePresence>
        {showMessage && messagerieOuverte && (
          <MessageForm
            key="message-modal"
            isOpen={showMessage}
            onClose={() => setShowMessage(false)}
            onUnreadCountChange={(count) => setUnreadMessageCount(count)}
          />
        )}
      </AnimatePresence>
      </AlertesCritiques>
    </div>
    )}
    </>
  );
};

export default AdministrationLayout;
